import { MutableRefObject, Ref, forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getCommercialFiltersMarkup } from '../../selectors/getCommercialFiltersMarkup';

import { WrapperWithBorder } from './internal/components/WrapperWithBorder';

export const CommercialFiltersContainer = forwardRef(function CommercialFilters(_, ref: Ref<HTMLDivElement>) {
  const { bodyHtml = '', entryPoint = null } = useSelector(getCommercialFiltersMarkup) || {};

  const nodeRef = useRef<HTMLDivElement | null>(null);
  const isEntryPointAppended = useRef<boolean>(false);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  /**
   * Бандл фильтров подключается только из браузера и только после гидрации: если он выполнится раньше,
   * React 19 при гидрации перезапишет содержимое узла с dangerouslySetInnerHTML и выбросит
   * уже отрисованное фильтрами дерево — фильтры останутся в разметке, но перестанут реагировать на клики.
   */
  useEffect(() => {
    if (!entryPoint || !nodeRef.current || isEntryPointAppended.current) {
      return;
    }

    isEntryPointAppended.current = true;

    const script = document.createElement('script');
    script.src = entryPoint.src;
    entryPoint.attributes.forEach(({ key, value }) => {
      if (value !== false) {
        script.setAttribute(key, value === true ? '' : value);
      }
    });

    nodeRef.current.appendChild(script);
  }, [entryPoint]);

  /**
   * Объект для dangerouslySetInnerHTML обязан быть стабильным по ссылке: React 19 сравнивает пропсы
   * по ссылке и на каждый ререндер заново присваивает innerHTML, выбрасывая дерево, которое
   * отрисовал внутри бандл фильтров. React 18 сравнивал саму html-строку, поэтому проблемы не было.
   */
  const markup = useMemo(() => ({ __html: bodyHtml }), [bodyHtml]);

  if (!bodyHtml) {
    return null;
  }

  return (
    <WrapperWithBorder>
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={markup} ref={setRef} suppressHydrationWarning={true} />
    </WrapperWithBorder>
  );
});

CommercialFiltersContainer.displayName = 'CommercialFiltersContainer';
